import React, { useState, useEffect } from 'react';
import { Player, RoundScore } from './types';
import { generatePlayerId, calculateTotalScore } from './utils/gameLogic';
import PlayerInput from './components/PlayerInput';
import ScoreBoard from './components/ScoreBoard';
import RoundInput from './components/RoundInput';
import GameComplete from './components/GameComplete';
import { Button } from './components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { ChevronsUpDown } from 'lucide-react';
import { auth } from './firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { GoogleSignInButton } from './components/GoogleSignInButton';

type GamePhase = 'setup' | 'playing' | 'complete';

const SESSION_KEY = 'oh-hell-game-state';

function getInitialState() {
  const saved = sessionStorage.getItem(SESSION_KEY);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      return {
        gamePhase: parsed.gamePhase || 'setup',
        players: parsed.players || [],
        currentRound: parsed.currentRound || 1,
        maxRounds: parsed.maxRounds || 0,
        dealerIndex: parsed.dealerIndex ?? null,
      };
    } catch {}
  }
  return {
    gamePhase: 'setup',
    players: [],
    currentRound: 1,
    maxRounds: 0,
    dealerIndex: null,
  };
}

const initial = getInitialState();

const App: React.FC = () => {
  const [gamePhase, setGamePhase] = useState<GamePhase>(initial.gamePhase);
  const [players, setPlayers] = useState<Player[]>(initial.players);
  const [currentRound, setCurrentRound] = useState(initial.currentRound);
  const [maxRounds, setMaxRounds] = useState(initial.maxRounds);
  const [dealerIndex, setDealerIndex] = useState<number | null>(initial.dealerIndex);
  const [currentBids, setCurrentBids] = useState<{ [playerId: string]: number }>({});
  const [currentTricks, setCurrentTricks] = useState<{ [playerId: string]: number }>({});
  const [user, setUser] = useState<User | null>(null);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, []);

  // Save to sessionStorage on state change
  useEffect(() => {
    const state = {
      gamePhase,
      players,
      currentRound,
      maxRounds,
      dealerIndex,
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  }, [gamePhase, players, currentRound, maxRounds, dealerIndex]);

  const addPlayer = (name: string) => {
    const newPlayer: Player = {
      id: generatePlayerId(),
      name,
      totalScore: 0,
      rounds: []
    };
    setPlayers(prev => [...prev, newPlayer]);
  };

  const startGame = () => {
    if (players.length >= 2) {
      const cardsPerPlayer = Math.floor(52 / players.length);
      setMaxRounds(cardsPerPlayer);
      setGamePhase('playing');
      // Randomly assign dealer
      const randomIndex = Math.floor(Math.random() * players.length);
      setDealerIndex(randomIndex);
    }
  };

  const updateCurrentRoundData = (bids: { [playerId: string]: number }, tricks: { [playerId: string]: number }) => {
    setCurrentBids(bids);
    setCurrentTricks(tricks);
  };

  const completeRound = (roundScores: RoundScore[]) => {
    const updatedPlayers = players.map(player => {
      const playerRoundScore = roundScores.find(score => score.playerId === player.id);
      if (playerRoundScore) {
        const newRounds = [...player.rounds, playerRoundScore];
        return {
          ...player,
          rounds: newRounds,
          totalScore: calculateTotalScore(newRounds)
        };
      }
      return player;
    });

    setPlayers(updatedPlayers);
    setCurrentBids({});
    setCurrentTricks({});

    if (currentRound >= maxRounds) {
      setGamePhase('complete');
    } else {
      setCurrentRound((prev: number) => prev + 1);
      // Rotate dealer
      setDealerIndex(prev => {
        if (prev === null) return 0;
        return (prev + 1) % players.length;
      });
    }
  };

  const startNewGame = () => {
    setPlayers([]);
    setCurrentRound(1);
    setMaxRounds(0);
    setGamePhase('setup');
    setDealerIndex(null);
    setCurrentBids({});
    setCurrentTricks({});
    sessionStorage.removeItem(SESSION_KEY);
  };

  const startNewGameWithSamePlayers = () => {
    // Keep the same players but reset their scores and rounds
    const resetPlayers = players.map(player => ({
      ...player,
      totalScore: 0,
      rounds: []
    }));
    
    setPlayers(resetPlayers);
    setCurrentRound(1);
    setMaxRounds(0);
    setGamePhase('playing');
    
    // Calculate max rounds for the new game
    const cardsPerPlayer = Math.floor(52 / resetPlayers.length);
    setMaxRounds(cardsPerPlayer);
    
    // Randomly assign dealer for the new game
    const randomIndex = Math.floor(Math.random() * resetPlayers.length);
    setDealerIndex(randomIndex);
  };

  // Calculate totals for progress bar
  const getTotalBids = () => Object.values(currentBids).reduce((sum, bid) => sum + bid, 0);
  const getTotalTricks = () => Object.values(currentTricks).reduce((sum, trick) => sum + trick, 0);
  const getMaxBidForCurrentRound = () => Math.min(currentRound, Math.floor(52 / players.length));

  return (
    <div className="max-w-6xl mx-auto px-5 pb-20">
      <header className="text-center mb-6 pt-4">
        <div className="flex justify-between items-center mb-4">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-white mb-2">Oh Hell Score Tracker</h1>
            <p className="text-white text-lg">Track your card game scores with ease!</p>
          </div>
          <div className="flex items-center gap-2">
            <GoogleSignInButton user={user} />
            <Popover>
              <PopoverTrigger>
                <Button variant="outline">
                  <ChevronsUpDown className="mr-2 h-4 w-4" />
                  Start Game
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="flex justify-center gap-2">
                  <Button variant="outline" onClick={startNewGame}>
                    New Game
                  </Button>
                  <Button variant="outline" onClick={startNewGameWithSamePlayers}>
                    Same Players
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </header>

      {gamePhase === 'setup' && (
        <>
          <PlayerInput onAddPlayer={addPlayer} players={players.map(p => p.name)} />
          {players.length >= 2 && (
            <div className="text-center">
              <button onClick={startGame} className="btn">
                Start Game with {players.length} Players
              </button>
            </div>
          )}
        </>
      )}

      {gamePhase === 'playing' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <ScoreBoard players={players} currentRound={currentRound} dealerIndex={dealerIndex} />
          </div>
          <div>
            <RoundInput
              players={players}
              currentRound={currentRound}
              onCompleteRound={completeRound}
              dealerIndex={dealerIndex}
              onUpdateCurrentRoundData={updateCurrentRoundData}
            />
          </div>
        </div>
      )}

      {gamePhase === 'complete' && (
        <GameComplete 
          players={players} 
          onNewGame={startNewGameWithSamePlayers} 
          onNewGameWithNewPlayers={startNewGame}
        />
      )}

      {/* Sticky Game Progress Bar */}
      {gamePhase === 'playing' && (
        <div className="fixed left-0 bottom-0 w-full bg-white shadow-lg border-t border-gray-200 z-50">
          <div className="max-w-6xl mx-auto px-5 flex justify-between items-center py-3">
            <div className="font-bold text-lg">
              Round {currentRound} of {maxRounds}
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm">
                <span className="font-bold">Bids:</span> {getTotalBids()}
              </div>
              <div className="text-sm">
                <span className="font-bold">Tricks:</span> {getTotalTricks()}
              </div>
              <div className="text-sm">
                {(() => {
                  const maxBid = getMaxBidForCurrentRound();
                  const totalBids = getTotalBids();
                  
                  // Always show bid status - how many bids are over/under the max
                  if (totalBids === maxBid) {
                    return <span className="text-red-600">Bids = Max (Invalid)</span>;
                  } else {
                    const bidDiff = totalBids - maxBid;
                    if (bidDiff > 0) {
                      return <span className="text-red-600">Bids +{bidDiff}</span>;
                    } else {
                      return <span className="text-blue-600">Bids {bidDiff}</span>;
                    }
                  }
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App; 